import React from 'react';

import { TableFoldButton } from '@components/admin/booking/TableFoldButton';
import { Checkbox } from '@components/Table/Checkbox';
import { invoiceStrings } from '@data/invoice';
import { AdminInquiryDetail } from '@models/BookingInquiry';
import { Invoice } from '@models/next/Invoice';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { createStrings } from '@utils/local';
import { url } from '@utils/pages';
import { formatDateTime, formatPrice } from '@utils/types';
import { useLocalStorageState } from '@utils/useLocalStorageState';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { z } from 'zod';

const strings = createStrings({
  ja: {
    details: '詳細',
    deadline: '期限',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  invoiceTable: {
    ...common.middleText,
    display: 'grid',
    gridTemplateColumns: '40px 40px 1fr 1fr 1fr 1fr 1.5fr repeat(7, 1fr) 60px',
    '& > div > div': {
      padding: '0.75rem',
      borderBottom: `1px solid ${theme.palette.grey[200]}`,
    },
    '&$folded': {
      gridTemplateColumns: '40px 40px 1fr 1fr 1fr 1fr 1.5fr repeat(4, 1fr) 60px',
    },
  },
  invoiceDetailsHead: {
    whiteSpace: 'pre',
    backgroundColor: theme.palette.base[200],
  },
  invoiceDetailsHeadWithBreak: {
    ...common.exSmallerText,
    ...common.textGray500,
    whiteSpace: 'pre',
    backgroundColor: theme.palette.base[200],
    paddingTop: '0.5rem !important',
    paddingBottom: '0 !important',
  },
  invoiceDetailsCell: {
    ...common.textGray700,
    '&$invoiceStatusTime': {
      paddingTop: 6,
      paddingBottom: 0,
    },
    '&$subsum': {
      ...common.textGray500,
    },
  },
  invoiceTimeTag: {
    ...common.textGray500,
    ...common.exSmallerText,
  },
  invoiceStatusTime: {},
  subsum: {},
  folded: {},
}));

export interface InvoiceTableProps {
  bookingInquiry: AdminInquiryDetail;
  items: Invoice[];
  onChecklistChange: (nextCheckList: InvoiceTableProps['checklist']) => any;
  checklist: Invoice['visit_id'][];
}

export const InvoiceTable: React.FC<InvoiceTableProps> = (props) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };
  const { bookingInquiry, items, checklist, onChecklistChange } = props;

  const [folded, setFolded] = useLocalStorageState('INVOICE_TABLE_FOLDED', z.boolean(), false);

  const allSelected = items.every((item) => checklist.includes(item.visit_id));

  const renderHeadWithBreak = (head: string, breakAt: number) => {
    return (
      <div className={classes.invoiceDetailsHeadWithBreak}>
        {head.slice(0, breakAt)}
        <br />
        {head.slice(breakAt)}
      </div>
    );
  };

  return (
    <div style={{ overflow: 'scroll' }}>
      <div className={clsx(classes.invoiceTable, folded && classes.folded)}>
        <div style={{ display: 'contents' }}>
          <div className={classes.invoiceDetailsHead}>
            <Checkbox
              value={allSelected}
              onChange={() => {
                if (allSelected) {
                  onChecklistChange([]);
                } else {
                  onChecklistChange(items.map((item) => item.visit_id));
                }
              }}
            />
          </div>
          <div className={classes.invoiceDetailsHead}>{invoiceStrings.no}</div>
          <div className={classes.invoiceDetailsHead}>{invoiceStrings.d2d_charge_no}</div>
          <div className={classes.invoiceDetailsHead}>{invoiceStrings.charge_no}</div>
          <div className={classes.invoiceDetailsHead}>{invoiceStrings.job_code}</div>
          <div className={classes.invoiceDetailsHead}>{invoiceStrings.charge_date}</div>
          <div className={classes.invoiceDetailsHead}>{invoiceStrings.client_id}</div>
          <div className={classes.invoiceDetailsHead}>{invoiceStrings.charge_pay_day}</div>

          <div className={classes.invoiceDetailsHead}>
            {invoiceStrings.total_price}
            <span style={{ marginLeft: 5 }} />
            <TableFoldButton folded={folded} onValueChange={setFolded} />
          </div>

          {!folded && (
            <>
              {renderHeadWithBreak(invoiceStrings.subsums.tax_excluded_total_price, 2)}
              {renderHeadWithBreak(invoiceStrings.subsums.excise_tax, 2)}
              {renderHeadWithBreak(invoiceStrings.subsums.tatekae_total_price, 2)}
            </>
          )}

          <div className={classes.invoiceDetailsHead}>{invoiceStrings.invoice_status}</div>
          <div className={classes.invoiceDetailsHead}>{invoiceStrings.payment_status}</div>
          <div className={classes.invoiceDetailsHead} />
        </div>

        {items.map((item, i) => {
          const selected = checklist.includes(item.visit_id);
          const paymentPastDue = item.charge_pay_day > dayjs().format('YYYY-MM-DD');

          return (
            <div key={item.id} className={classes.contentsHoverTextPrimary}>
              <div className={classes.invoiceDetailsCell}>
                <Checkbox
                  value={selected}
                  onChange={() => {
                    if (selected) {
                      onChecklistChange(checklist.filter((id) => id !== item.visit_id));
                    } else {
                      onChecklistChange([...checklist, item.visit_id]);
                    }
                  }}
                />
              </div>
              <div className={classes.invoiceDetailsCell}>{i + 1}</div>
              <div className={clsx(classes.invoiceDetailsCell, classes.whitespacePre)}>
                {item.d2d_charge_no || '-'}
              </div>
              <div className={clsx(classes.invoiceDetailsCell, classes.whitespacePre)}>
                {item.charge_no || '-'}
              </div>
              <div className={clsx(classes.invoiceDetailsCell, classes.whitespacePre)}>
                {item.job_code || '-'}
              </div>
              <div className={clsx(classes.invoiceDetailsCell, classes.whitespacePre)}>
                {item.charge_date || '-'}
              </div>
              <div className={classes.invoiceDetailsCell}>{item.client_name || '-'}</div>
              <div className={clsx(classes.invoiceDetailsCell, classes.whitespacePre)}>
                {item.charge_pay_day || '-'}
              </div>

              <div className={clsx(classes.invoiceDetailsCell, classes.whitespacePre)}>
                {formatPrice(Number(item.total_price), item.total_price_currency) || '-'}
              </div>

              {!folded && (
                <>
                  <div
                    className={clsx(
                      classes.invoiceDetailsCell,
                      classes.whitespacePre,
                      classes.textGray500Important,
                    )}
                  >
                    {formatPrice(item.tax_excluded_total_price, item.total_price_currency)}
                  </div>

                  <div
                    className={clsx(
                      classes.invoiceDetailsCell,
                      classes.whitespacePre,
                      classes.textGray500Important,
                    )}
                  >
                    {formatPrice(item.excise_tax, item.total_price_currency)}
                  </div>

                  <div
                    className={clsx(
                      classes.invoiceDetailsCell,
                      classes.whitespacePre,
                      classes.textGray500Important,
                    )}
                  >
                    {formatPrice(item.tatekae_total_price, item.total_price_currency)}
                  </div>
                </>
              )}

              <div
                className={clsx(
                  classes.invoiceDetailsCell,
                  classes.invoiceStatusTime,
                  item.invoice_status === '修正中' && classes.textErrorImportant,
                )}
              >
                {item.invoice_status}

                <div
                  className={clsx(
                    classes.invoiceTimeTag,
                    item.invoice_status === '修正中' && classes.textErrorImportant,
                  )}
                >
                  {item.invoice_status === '作成中' && formatDateTime(item.created_at)}
                  {item.invoice_status === '修正中' &&
                    item.review_time &&
                    formatDateTime(item.review_time)}
                  {item.invoice_status === '発行済' &&
                    item.send_time &&
                    formatDateTime(item.send_time)}
                </div>
              </div>
              <div
                className={clsx(
                  classes.invoiceDetailsCell,
                  classes.invoiceStatusTime,
                  paymentPastDue && classes.textErrorImportant,
                )}
              >
                {item.payment_status}

                <div
                  className={clsx(
                    classes.invoiceTimeTag,
                    paymentPastDue && classes.textErrorImportant,
                  )}
                >
                  {item.payment_status === '未' && `${strings.deadline}: ${item.charge_pay_day}`}
                  {item.payment_status === '済' &&
                    `${invoiceStrings.finish_date}: ${item.finish_date ?? '-'}`}
                </div>
              </div>
              <div className={classes.invoiceDetailsCell}>
                <a
                  href={`${url('/admin/booking/InvoiceDetail')}?d2dNo=${bookingInquiry.d2d_no}&id=${
                    item.id
                  }`}
                  target="_blank"
                  rel="noreferrer noopener"
                  className={classes.link}
                >
                  {strings.details}
                </a>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
